/**
 * Transactions du grand livre — toutes les écritures d'une période, regroupées par compte avec solde courant.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { JOURNAL_CLS, Pill, money } from "../_shared";
import { Banner, Num, TH, THead, Table } from "./_bits";

function AccountBlock({ a }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border rounded-xl overflow-hidden" data-testid={`gltx-account-${a.number}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full px-4 py-2 border-b bg-slate-50 flex items-center justify-between gap-2 text-left hover:bg-slate-100" data-testid={`gltx-toggle-${a.number}`}>
        <span className="flex items-center gap-2 text-sm"><span className="text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span><span className="font-mono text-xs text-slate-500">{a.number}</span><span className="font-bold text-slate-800">{a.name}</span><span className="text-xs text-slate-400">({a.rows.length} mouvement{a.rows.length > 1 ? "s" : ""})</span></span>
        <span className="text-xs text-slate-600 tabular-nums flex gap-4"><span>Ouv. <strong>{money(a.opening_balance)}</strong></span><span>Dt <strong>{money(a.total_debit)}</strong></span><span>Ct <strong>{money(a.total_credit)}</strong></span><span>Fin <strong className="text-emerald-700">{money(a.closing_balance)}</strong></span></span>
      </button>
      {open && (
        <Table>
          <THead><TH className="w-24">Date</TH><TH className="w-14">N°</TH><TH className="w-16">Jrn</TH><TH>Description</TH><TH right className="w-28">Débit</TH><TH right className="w-28">Crédit</TH><TH right className="w-32">Solde</TH></THead>
          <tbody className="divide-y">
            <tr className="text-slate-500 italic"><td className="px-3 py-1.5" colSpan={6}>Solde d'ouverture</td><Num v={a.opening_balance} blankZero={false} /></tr>
            {a.rows.map((r, i) => (
              <tr key={i} className="hover:bg-emerald-50/40">
                <td className="px-3 py-1.5 font-mono text-xs">{r.date}</td><td className="px-3 py-1.5 font-mono text-xs">{r.no}</td>
                <td className="px-3 py-1.5"><Pill cls={JOURNAL_CLS[r.journal]}>{r.journal}</Pill></td>
                <td className="px-3 py-1.5"><div className="truncate max-w-md">{r.memo}</div>{r.description && r.description !== r.memo && <div className="text-[11px] text-slate-400 truncate max-w-md">{r.description}</div>}</td>
                <Num v={r.debit} /><Num v={r.credit} /><Num v={r.balance} blankZero={false} className="font-semibold" />
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default function GlTransactionsReport({ data }) {
  return (
    <div className="space-y-3" data-testid="report-gl-transactions">
      <p className="text-xs text-slate-500"><strong>{data.nb_entries}</strong> écriture(s) du {data.date_from} au {data.date_to} · {data.accounts.length} compte(s) mouvementé(s)</p>
      {data.accounts.map((a) => <AccountBlock key={a.account_id} a={a} />)}
      {!data.accounts.length && <p className="text-sm text-slate-400 text-center py-8">Aucune écriture pour cette période.</p>}
      <Banner ok label={`Total des mouvements — Débits ${money(data.total_debit)} · Crédits ${money(data.total_credit)}`} value={Math.abs(data.total_debit - data.total_credit) < 0.01 ? "Équilibré ✓" : "Écart !"} testId="gltx-totals" />
    </div>
  );
}
